import { Component, inject, Input } from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { format } from 'date-fns';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { ExportService } from '../../../services/export.service';
import { ToastrService } from 'ngx-toastr';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-export',
  standalone: true,
  imports: [NgbDropdownModule, TranslocoPipe],
  templateUrl: './export.component.html',
  styleUrl: './export.component.scss'
})
export class ExportComponent {
  private exportService: ExportService = inject(ExportService);
  private transloco = inject(TranslocoService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);

  @Input() request: any;
  @Input() disabled: boolean = false;
  @Input() filePrefix: string = 'Export';
  @Input() endpoint: string;

  isExporting: boolean = false;

  async exportExcel(): Promise<void> {
    await this.doExport('xlsx');
  }

  async exportCsv(): Promise<void> {
    await this.doExport('csv');
  }

  private async doExport(extension: 'xlsx' | 'csv'): Promise<void> {
    this.isExporting = true;

    try {
      const blob = await this.exportService.export(this.request, this.endpoint, extension);

      if (blob.type?.includes('application/json')) {
        const text = await blob.text();
        const errorResponse = JSON.parse(text);
        const errorMessage = errorResponse.message || this.transloco.translate('shared.export.failed');
        this.errorService.handleError({ error: { errorCode: errorResponse.errorCode, message: errorMessage } });
        return;
      }

      this.downloadBlob(blob, extension);
      this.toastr.success(this.transloco.translate('shared.export.success'));
    } catch (error: any) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isExporting = false;
        }
      });
    } finally {
      this.isExporting = false;
    }
  }

  private downloadBlob(blob: Blob, extension: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.filePrefix}_${format(new Date(), 'yyyy-MM-dd HH-mm-ss')}.${extension}`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}